"use client";

import { zodResolver } from "@hookform/resolvers/zod";
import Link from "next/link";
import { signIn } from "next-auth/react";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { PATH_AUTH } from "~/routes/paths";
import { Button } from "~/shadcn/ui/button";
import { Input } from "~/shadcn/ui/input";
import { Label } from "~/shadcn/ui/label";
import { api } from "~/trpc/react";

type Props = {
  lang: AppLocale;
  verifiedState?: "success" | "invalid";
  verificationEmail?: string;
};

const CredentialsSchema = z.object({
  email: z.string().trim().toLowerCase().email(),
  password: z.string().min(1).max(128),
});

type CredentialsSchema = z.infer<typeof CredentialsSchema>;
const RESEND_COOLDOWN_SECONDS = 60;

export default function AuthWithCredentials({
  lang,
  verifiedState,
  verificationEmail,
}: Props) {
  const callbackUrl = `/${lang}/dashboard`;
  const [feedback, setFeedback] = useState<null | {
    type: "error";
    message: string;
  }>(null);
  const [resendFeedback, setResendFeedback] = useState<string | null>(null);
  const [resendCooldown, setResendCooldown] = useState(0);
  const {
    register,
    getValues,
    handleSubmit,
    formState: { isSubmitting, errors },
  } = useForm<CredentialsSchema>({
    resolver: zodResolver(CredentialsSchema),
    defaultValues: {
      email: verificationEmail ?? "",
      password: "",
    },
  });
  const resendMutation = api.auth.resendVerification.useMutation();

  // The resend countdown subscribes to the browser clock.
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = setInterval(() => {
      setResendCooldown((prev) => Math.max(0, prev - 1));
    }, 1000);
    return () => clearInterval(timer);
  }, [resendCooldown]);

  const onSubmit = async (values: CredentialsSchema) => {
    setFeedback(null);
    try {
      const res = await signIn("credentials", {
        email: values.email,
        password: values.password,
        callbackUrl,
        redirect: false,
      });
      if (res?.error || !res?.url) {
        setFeedback({
          type: "error",
          message:
            lang === "sv"
              ? "Fel mejl eller lösenord, eller så är e-posten inte verifierad ännu."
              : "Wrong email/password, or the email is not verified yet.",
        });
        return;
      }
      window.location.assign(res.url);
    } catch (submitError) {
      setFeedback({
        type: "error",
        message:
          submitError instanceof Error
            ? submitError.message
            : lang === "sv"
              ? "Något gick fel."
              : "Something went wrong.",
      });
    }
  };

  const handleResendVerification = async () => {
    const email = z
      .string()
      .trim()
      .toLowerCase()
      .email()
      .safeParse(getValues("email"));
    if (!email.success || resendCooldown > 0) {
      if (!email.success)
        setResendFeedback(
          lang === "sv"
            ? "Ange en giltig e-postadress först."
            : "Enter a valid email address first.",
        );
      return;
    }
    setResendFeedback(null);
    try {
      const payload = await resendMutation.mutateAsync({
        email: email.data,
        lang,
      });
      if (!payload.ok && payload.code === "RATE_LIMITED") {
        setResendFeedback(
          lang === "sv"
            ? `För många försök. Försök igen om ${payload.retryAfter ?? 60} sekunder.`
            : `Too many attempts. Try again in ${payload.retryAfter ?? 60} seconds.`,
        );
        if (payload.retryAfter) {
          setResendCooldown(payload.retryAfter);
        }
        return;
      }
      setResendFeedback(
        lang === "sv"
          ? "Om det finns ett overifierat konto skickas en ny verifieringslänk."
          : "If an unverified account exists, a new verification link will be sent.",
      );
      setResendCooldown(RESEND_COOLDOWN_SECONDS);
    } catch (error) {
      setResendFeedback(
        error instanceof Error
          ? error.message
          : lang === "sv"
            ? "Kunde inte skicka verifieringslänken."
            : "Could not resend the verification link.",
      );
    }
  };

  return (
    <div className="flex flex-col gap-5">
      {verifiedState === "success" && (
        <p className="rounded-md border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-700 dark:border-green-900/50 dark:bg-green-950/30 dark:text-green-400">
          {lang === "sv"
            ? "Din e-post är verifierad. Du kan nu logga in."
            : "Your email is verified. You can now sign in."}
        </p>
      )}
      {verifiedState === "invalid" && (
        <div className="rounded-md border border-destructive/20 bg-destructive/5 px-3 py-3 text-sm text-destructive">
          <p>
            {lang === "sv"
              ? "Verifieringslänken är ogiltig eller har gått ut."
              : "The verification link is invalid or expired."}
          </p>
        </div>
      )}
      <form
        noValidate
        className="flex flex-col gap-4"
        onSubmit={handleSubmit(onSubmit)}
      >
        <div className="grid gap-2">
          <Label className="font-medium" htmlFor="credential-email">
            Email
          </Label>
          <Input
            className="h-12 text-base"
            id="credential-email"
            type="email"
            autoComplete="username"
            aria-invalid={Boolean(errors.email)}
            aria-describedby={errors.email ? "email-error" : undefined}
            {...register("email")}
          />
          {errors.email && (
            <p
              id="email-error"
              role="alert"
              className="text-sm text-destructive"
            >
              {lang === "sv"
                ? "Ange en giltig e-postadress."
                : "Enter a valid email address."}
            </p>
          )}
        </div>
        <div className="grid gap-2">
          <Label className="font-medium" htmlFor="credential-password">
            {lang === "sv" ? "Lösenord" : "Password"}
          </Label>
          <Input
            className="h-12 text-base"
            id="credential-password"
            type="password"
            autoComplete="current-password"
            aria-invalid={Boolean(errors.password)}
            aria-describedby={errors.password ? "password-error" : undefined}
            {...register("password")}
          />
        </div>

        {errors.password && (
          <p
            id="password-error"
            role="alert"
            className="text-sm text-destructive"
          >
            {lang === "sv"
              ? "Ange ditt lösenord (högst 128 tecken)."
              : "Enter your password (at most 128 characters)."}
          </p>
        )}
        {feedback?.type === "error" && (
          <p className="text-sm text-destructive">{feedback.message}</p>
        )}

        <Button
          className="h-12 text-base"
          type="submit"
          disabled={isSubmitting}
        >
          {isSubmitting
            ? lang === "sv"
              ? "Loggar in..."
              : "Signing in..."
            : lang === "sv"
              ? "Logga in med e-post"
              : "Sign in with email"}
        </Button>
      </form>

      <div className="grid gap-3">
        <Link
          href={`/${lang}/auth/reset-password`}
          className="text-sm underline"
        >
          {lang === "sv" ? "Glömt lösenord?" : "Forgot password?"}
        </Link>
        <Button
          type="button"
          variant="outline"
          onClick={handleResendVerification}
          disabled={resendCooldown > 0 || resendMutation.isPending}
        >
          {resendCooldown > 0
            ? lang === "sv"
              ? `Skicka igen om ${resendCooldown}s`
              : `Resend in ${resendCooldown}s`
            : lang === "sv"
              ? "Skicka verifieringsmejl igen"
              : "Resend verification email"}
        </Button>
        {resendFeedback && (
          <output className="text-sm text-muted-foreground">
            {resendFeedback}
          </output>
        )}
      </div>

      <p className="text-sm text-muted-foreground">
        {lang === "sv" ? "Ny användare?" : "New user?"}{" "}
        <Link href={PATH_AUTH.register(lang)} className="underline">
          {lang === "sv" ? "Skapa konto" : "Create account"}
        </Link>
      </p>
    </div>
  );
}
